import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Badge, Modal, Form, Input, Switch, Select, message, Divider, Button } from 'antd';
import {
    DashboardOutlined,
    ProjectOutlined,
    FileTextOutlined,
    DollarOutlined,
    UserOutlined,
    LogoutOutlined,
    SettingOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    BarChartOutlined,
    TransactionOutlined,
    CreditCardOutlined,
    UnorderedListOutlined,
    CalendarOutlined,
    BankOutlined,
    FundOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setCredentials } from '../../store/authSlice';
import { setTheme } from '../../store/settingsSlice';
import { getUserRoles } from '../../utils/permissions';
import { authAPI } from '../../services/api';
import axios from 'axios';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MainLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { theme } = useSelector((state) => state.settings);
    
    const [profileModalVisible, setProfileModalVisible] = useState(false);
    const [settingsModalVisible, setSettingsModalVisible] = useState(false);
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isCardOwner, setIsCardOwner] = useState(false);

    // 역할 확인 함수
    const checkRole = (user, roleName) => {
        if (!user) return false;

        let rolesArray = [];
        if (user.roles) {
            if (Array.isArray(user.roles)) {
                rolesArray = [...user.roles];  // ← 새 배열로 복사
            } else if (typeof user.roles === 'string') {
                try {
                    rolesArray = JSON.parse(user.roles);
                } catch (e) {
                    rolesArray = [];
                }
            }
        }
        if (user.role) rolesArray.push(user.role);  // ← 새 배열이므로 정상 동작

        return rolesArray.includes(roleName);
    };

    // 카드 소유자 확인 함수
    const checkCardOwnership = async () => {
        if (!user) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/corporate-cards/cards`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const hasCards = response.data.cards && response.data.cards.length > 0;
            setIsCardOwner(hasCards);
        } catch (error) {
            console.error('카드 소유 확인 실패:', error);
            setIsCardOwner(false);
        }
    };

    // 카드 소유 여부 확인
    useEffect(() => {
        if (user) {
            checkCardOwnership();
        }
    }, [user]);

    // 역할별 메뉴 구성
    const getMenuItems = () => {
        const isFinance = checkRole(user, 'finance');
        const isPM = checkRole(user, 'pm');
        const isManager = checkRole(user, 'manager');
        const isAdmin = checkRole(user, 'admin');

        const menuItems = [
            // ⭐ 개인 대시보드 (모든 사용자)
            {
                key: '/dashboard',
                icon: <DashboardOutlined />,
                label: '대시보드',
            },
            // ⭐ 전략회의 (PM/부서장/관리자/재무담당만)
            ...(isAdmin || isManager || isPM || isFinance ? [{
                key: '/strategic-meeting',
                icon: <FundOutlined />,
                label: '전략회의',
            }] : []),
        ];

        // ⭐ 프로젝트 관리 (PM/부서장/관리자만)
        if (isAdmin || isManager || isPM) {
            menuItems.push({
                key: '/projects-menu',
                icon: <ProjectOutlined />,
                label: '프로젝트 관리',
                children: [
                    {
                        key: '/projects',
                        icon: <UnorderedListOutlined />,
                        label: '프로젝트 현황',
                    },
                    {
                        key: '/approvals/worklog',
                        icon: <FileTextOutlined />,
                        label: '작업일지 승인',
                    },
                    {
                        key: '/approvals/expense',
                        icon: <DollarOutlined />,
                        label: '경비 승인',
                    },
                ],
            });
        }

        // ⭐ 재무 관리 (재무담당/PM/부서장/관리자 또는 카드 오너)
        if (isFinance || isPM || isManager || isAdmin || isCardOwner) {
            const financeMenuItems = [];

            // 재무담당/PM/부서장/관리자만 재무 대시보드와 프로젝트 수입/지출 접근 가능
            if (isFinance || isPM || isManager || isAdmin) {
                financeMenuItems.push(
                    {
                        key: '/finance/dashboard',
                        icon: <BarChartOutlined />,
                        label: '재무 대시보드',
                    },
                    {
                        key: '/finance/transactions',
                        icon: <TransactionOutlined />,
                        label: '프로젝트 수입/지출',
                    }
                );
            }

            // 재무담당과 관리자만 경비 입금 처리 가능
            if (isFinance || isAdmin) {
                financeMenuItems.push({
                    key: '/finance/approval',
                    icon: <CreditCardOutlined />,
                    label: '경비 입금 처리',
                });
            }

            // 재무담당/PM/부서장/관리자 또는 카드 오너가 법인카드 관리 메뉴에 접근 가능
            financeMenuItems.push({
                key: '/finance/corporate-cards',
                icon: <BankOutlined />,
                label: '법인카드 관리',
            });

            // 관리자와 부서장은 근태 현황 관리 가능
            if (isAdmin || isManager) {
                financeMenuItems.push({
                    key: '/finance/attendance',
                    icon: <CalendarOutlined />,
                    label: '근태 현황 관리',
                });
            }

            // 메뉴 아이템이 하나라도 있으면 재무 관리 메뉴 추가
            if (financeMenuItems.length > 0) {
                menuItems.push({
                    key: '/finance',
                    icon: <DollarOutlined />,
                    label: '재무 관리',
                    children: financeMenuItems,
                });
            }
        }

        // ⭐ 시스템 관리 (관리자만)
        if (isAdmin) {
            menuItems.push({
                key: '/admin',
                icon: <SettingOutlined />,
                label: '시스템 관리',
                children: [
                    {
                        key: '/admin/users',
                        icon: <UserOutlined />,
                        label: '사용자 관리',
                    },
                    {
                        key: '/admin/departments',
                        icon: <TeamOutlined />,
                        label: '부서 관리',
                    },
                ],
            });
        }

        return menuItems;
    };

    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: '내 정보',
        },
        {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '설정',
        },
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: '로그아웃',
            danger: true,
        },
    ];

    const handleMenuClick = ({ key }) => {
        navigate(key);
    };

    const handleUserMenuClick = ({ key }) => {
        if (key === 'logout') {
            dispatch(logout());
            navigate('/login');
        } else if (key === 'profile') {
            setProfileModalVisible(true);
            profileForm.setFieldsValue({
                name: user.name,
                email: user.email,
                phone: user.phone,
                employee_id: user.employee_id
            });
        } else if (key === 'settings') {
            setSettingsModalVisible(true);
        }
    };

    const handleProfileUpdate = async (values) => {
        setLoading(true);
        try {
            const response = await authAPI.updateProfile(values);
            dispatch(setCredentials({ user: response.data.user, token: localStorage.getItem('token') }));
            message.success('프로필이 업데이트되었습니다');
            setProfileModalVisible(false);
        } catch (error) {
            message.error(error.response?.data?.message || '프로필 업데이트 실패');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (values) => {
        setLoading(true);
        try {
            await authAPI.changePassword(values);
            message.success('비밀번호가 변경되었습니다');
            passwordForm.resetFields();
        } catch (error) {
            message.error(error.response?.data?.message || '비밀번호 변경 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleThemeChange = (newTheme) => {
        dispatch(setTheme(newTheme));
        message.success(`${newTheme === 'dark' ? '다크' : '라이트'} 모드로 변경되었습니다`);
    };

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    // 역할 배지 생성
    const getRoleBadges = () => {
        const roles = getUserRoles(user);
        const roleColors = {
            admin: '#f50',
            finance: '#2db7f5',
            manager: '#87d068',
            pm: '#108ee9',
            user: '#999',
        };

        const roleNames = {
            admin: '관리자',
            finance: '재무',
            manager: '부서장',
            pm: 'PM',
            user: '사원',
        };

        // 우선순위 순서로 정렬
        const priorityOrder = ['admin', 'finance', 'manager', 'pm', 'user'];
        const sortedRoles = [...roles].sort((a, b) =>
            priorityOrder.indexOf(a) - priorityOrder.indexOf(b)
        );

        // 최대 2개까지만 표시
        const displayRoles = sortedRoles.slice(0, 2);

        return displayRoles.map(role => (
            <Badge
                key={role}
                count={roleNames[role] || role}
                style={{
                    backgroundColor: roleColors[role] || '#999',
                    marginRight: 4
                }}
            />
        ));
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                width={250}
                style={{
                    background: theme === 'dark' ? '#2a2a2a' : '#fff',
                    borderRight: `1px solid ${theme === 'dark' ? '#434343' : '#f0f0f0'}`,
                }}
            >
                <div style={{
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: `1px solid ${theme === 'dark' ? '#434343' : '#f0f0f0'}`,
                }}>
                    <Text strong style={{ fontSize: 18, color: '#667eea' }}>
                        아신씨엔티-스마트워크
                    </Text>
                </div>
                <Menu
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={getMenuItems()}
                    onClick={handleMenuClick}
                    style={{ border: 'none' }}
                />
            </Sider>

            <Layout>
                <Header style={{
                    background: theme === 'dark' ? '#2a2a2a' : '#fff',
                    padding: '0 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme === 'dark' ? '#434343' : '#f0f0f0'}`,
                }}>
                    <div />
                    <Dropdown
                        menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                        placement="bottomRight"
                    >
                        <Space style={{ cursor: 'pointer' }} size="small">
                            {getRoleBadges()}
                            <Avatar icon={<UserOutlined />} size="small" />
                            <div style={{
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                <Text strong style={{ fontSize: 14 }}>{user?.name}</Text>
                            </div>
                        </Space>
                    </Dropdown>
                </Header>

                <Content style={{
                    margin: '24px',
                    background: theme === 'dark' ? '#1f1f1f' : '#fff',
                    padding: 24,
                    borderRadius: 8,
                    color: theme === 'dark' ? '#fff' : '#000',
                }}>
                    {children}
                </Content>
            </Layout>
            
            {/* 프로필 모달 */}
            <Modal
                title="내 정보"
                open={profileModalVisible}
                onCancel={() => setProfileModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={profileForm}
                    layout="vertical"
                    onFinish={handleProfileUpdate}
                >
                    <Form.Item
                        name="name"
                        label="이름"
                        rules={[{ required: true, message: '이름을 입력하세요' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="이메일"
                        rules={[{ required: true, type: 'email', message: '올바른 이메일을 입력하세요' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label="전화번호"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="employee_id"
                        label="사번"
                    >
                        <Input disabled />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setProfileModalVisible(false)}>취소</Button>
                            <Button type="primary" htmlType="submit" loading={loading}>저장</Button>
                        </Space>
                    </div>
                </Form>
                
                <Divider>비밀번호 변경</Divider>
                
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handlePasswordChange}
                >
                    <Form.Item
                        name="currentPassword"
                        label="현재 비밀번호"
                        rules={[{ required: true, message: '현재 비밀번호를 입력하세요' }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="newPassword"
                        label="새 비밀번호"
                        rules={[
                            { required: true, message: '새 비밀번호를 입력하세요' },
                            { min: 6, message: '비밀번호는 6자리 이상이어야 합니다' }
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="비밀번호 확인"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: '비밀번호를 다시 입력하세요' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('비밀번호가 일치하지 않습니다'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Button type="primary" htmlType="submit" loading={loading}>비밀번호 변경</Button>
                    </div>
                </Form>
            </Modal>
            
            {/* 설정 모달 */}
            <Modal
                title="설정"
                open={settingsModalVisible}
                onCancel={() => setSettingsModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form layout="vertical">
                    <Form.Item label="테마 설정">
                        <Select
                            value={theme}
                            onChange={handleThemeChange}
                            style={{ width: '100%' }}
                        >
                            <Select.Option value="light">라이트 모드</Select.Option>
                            <Select.Option value="dark">다크 모드</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="알림 설정">
                        <Switch
                            checked={true}
                            checkedChildren="ON"
                            unCheckedChildren="OFF"
                        />
                        <div style={{ marginTop: 8, color: '#666' }}>
                            브라우저 알림을 허용합니다
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default MainLayout;